import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { margin, styled } from "@mui/system";
import {Box, Grid, MenuItem, Select, Typography,Tabs,Tab } from "@mui/material";
import { ArrowDropDownCircle, ArrowUpward, AutoAwesome, TrendingDown, TrendingDownRounded, TrendingUp, TrendingUpSharp } from "@mui/icons-material";
import ailoader from "../../assets/ailoader.gif";
import gs from "../../assets/gs.png";
import { Tooltip as MUITooltip } from '@mui/material';
import { useAtom } from 'jotai';
import { selectedCategoryAtomFamily } from "./atoms";
import { useFileContext } from "./FileContext";


const Container = styled("div")({
  // backgroundColor: "#FFFFFF08",
  border: "1px solid #FFFFFF0F",
  borderRadius: "10px",
  padding: "20px",
  color: "#fff",
  fontStyle: 'Roboto',
});

const LegendWrapper = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(3, auto)", 
  gap: "16px",
  justifyContent: "center",
  marginTop: "16px",
  background: "#FFFFFF0F",
  borderRadius: "4px",
  padding: "8px",
  color: "#fff",
  fontSize: "12px",
  fontStyle: "Roboto",
});

const MovementContainer = styled(Box)({
  // backgroundColor: "#FFFFFF0F",
  borderRadius: "10px",
  padding: "16px",
  height: '100%',
  overflowY: 'auto'
});

const MovementItem = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
  padding: '8px',
  backgroundColor: "#FFFFFF08",
  borderRadius: '6px'
});

export const CommodityPriceChart = ({ filteredResults, costStructureMovements, selectedL3Category }) => {
  console.log('cost structur movements for CM is', costStructureMovements)
  console.log('filtered results for CM is', filteredResults)

  const fileContext = useFileContext();
  const filenameKey = fileContext?.filename ?? "global";
  const [selectedCategory, setSelectedCategory] = useAtom(
    selectedCategoryAtomFamily(filenameKey)
  );
  
  const LoadingGif = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "50px",
        width: "auto",
      }}
    >
      <img
        src={ailoader}
        alt="Loading..."
        style={{ width: "30px", height: "30px" }}
      />
    </Box>
  )
  
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const formatDate = (dateStr) => {
    try {
      const date = dateStr.includes("/")
        ? new Date(dateStr.split("/").reverse().join("-"))
        : new Date(dateStr);

      if (isNaN(date.getTime())) {
        console.warn("Invalid date:", dateStr);
        return dateStr;
      }

      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear().toString().slice(-2);
      return `${month}-${year}`;
    } catch (error) {
      console.warn("Error formatting date:", dateStr, error);
      return dateStr;
    }
  };

  const renderChart = () => {
    // Navigate through the hierarchical structure to find the selected category
    if (!filteredResults || !selectedL3Category) return null;
    
    // Find the L1 category that contains the selected L3 category
    let selectedCategoryData = null;
    
    // Loop through all L1 categories
    Object.keys(filteredResults).forEach(l1Key => {
      const l1Category = filteredResults[l1Key];
      
      // Loop through L2 categories
      Object.keys(l1Category).forEach(l2Key => {
        if (l2Key === 'Prototype') return; // Skip prototype keys
        
        const l2Category = l1Category[l2Key];
        
        // Check if this L2 category matches our selected L3 category
        if (l2Key === selectedL3Category && l2Category && l2Category.series) {
          selectedCategoryData = l2Category;
        }
      });
    });

    if (!selectedCategoryData) return null;

    const allSeries = selectedCategoryData.series;
    const mainCategoryData = allSeries.find(
      (series) => series.type === "main_category"
    );
    const costMaterialData = allSeries.filter(
      (series) => series.type === "cost_structure"
    );

    const dateMap = new Map();

    if (mainCategoryData && mainCategoryData.series_data?.content?.points) {
      mainCategoryData.series_data.content.points.forEach((point) => {
        dateMap.set(point.date, {
          date: formatDate(point.date),
          [mainCategoryData["Series Name"]]: point.value,
        });
      });
    }

    costMaterialData.forEach((costMaterial) => {
      if (costMaterial.series_data?.content?.points) {
        costMaterial.series_data.content.points.forEach((point) => {
          const existingPoint = dateMap.get(point.date) || {
            date: formatDate(point.date),
          };
          existingPoint[costMaterial["Series Name"]] = point.value;
          dateMap.set(point.date, existingPoint);
        });
      }
    });

    let chartData = Array.from(dateMap.values())
      .map((item) => {
        const [month, year] = item.date.split("-");
        const monthIndex = new Date(`${month} 1`).getMonth();
        const fullDate = new Date(parseInt(`20${year}`), monthIndex, 1);
        return { ...item, fullDate };
      })
      .sort((a, b) => a.fullDate - b.fullDate)
      .map(({ fullDate, ...rest }) => rest);

    // Smoothing function
    const smoothData = (data, seriesName, smoothingFactor = 0.3) => {
      return data.map((point, index) => {
        if (index === 0) return point;
        return {
          ...point,
          [seriesName]: 
            point[seriesName] * smoothingFactor + 
            data[index - 1][seriesName] * (1 - smoothingFactor)
        };
      });
    };

    // Apply smoothing to each series
    chartData = chartData.map(dataPoint => {
      const smoothedPoint = { ...dataPoint };
      
      // Smooth main category if it exists
      if (mainCategoryData && mainCategoryData["Series Name"]) {
        const seriesName = mainCategoryData["Series Name"];
        if (smoothedPoint[seriesName]) {
          smoothedPoint[seriesName] = smoothData(
            chartData, 
            seriesName
          ).find(p => p.date === dataPoint.date)[seriesName];
        }
      }

      // Smooth cost material series
      costMaterialData.forEach((costMaterial) => {
        const seriesName = costMaterial["Series Name"];
        if (smoothedPoint[seriesName]) {
          smoothedPoint[seriesName] = smoothData(
            chartData, 
            seriesName
          ).find(p => p.date === dataPoint.date)[seriesName];
        }
      });

      return smoothedPoint;
    });

    const colors = [
      "#FFC107", "#FF5722", "#4CAF50", "#03A9F4", 
      "#E91E63", "#9C27B0", "#673AB7", "#00BCD4", 
      "#8BC34A", "#FF9800"
    ];

    return (
      <ResponsiveContainer width="100%" height={421}>
        <LineChart data={chartData}>
          <XAxis
            dataKey="date"
            axisLine={{ stroke: '#FFFFFF', strokeWidth: 1 }}
            tick={{ fill: "#FFFFFF" }}
            style={{ fontSize: "12px", fontStyle: "Roboto" }}
            padding={{ left: 30 }}
            interval="preserveStartEnd"
            allowDuplicatedCategory={false}
          />
          
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#FFFFFF" }}
            tickFormatter={(value) => Math.round(value)}
            style={{ fontSize: "12px", fontStyle: "Roboto" }}
            domain={['dataMin - 10', 'dataMax + 10']}
            tickCount={6}
            interval={2}
            label={{
              value: "Price Trend",
              angle: -90,
              position: "insideLeft",
              style: {
                fill: "#FFFFFF",
                fontSize: "12px",
                fontStyle: 'Roboto',
                textAnchor: "middle",
              },
            }}
          />
          
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF08",
              color: "#fff",
              fontSize: "12px",
              fontStyle: 'Roboto',
              border: "1px solid #FFFFFF0F",
            }}
            cursor={{ stroke: "#555", strokeWidth: 1 }}
            formatter={(value, name) => [Number(value.toFixed(2)), name]}
          />
          
          <Legend
            content={({ payload }) => (
              <LegendWrapper>
                {payload.map((entry, index) => {
                  // Check if there are multiple opening brackets
                  const hasMultipleOpeningBrackets = (entry.value.match(/\(/g) || []).length > 1;

                  // If there are multiple opening brackets, split from the last one
                  let seriesNameWithoutUnit;
                  if (hasMultipleOpeningBrackets) {
                    const lastOpeningBracketIndex = entry.value.lastIndexOf('(');
                    seriesNameWithoutUnit = entry.value.substring(0, lastOpeningBracketIndex).trim();
                  } else {
                    const match = entry.value.match(/^(.*?)(?: \(([^)]*)\))?$/);
                    seriesNameWithoutUnit = match ? match[1] : entry.value;
                  }

                  // Find the series with a more flexible search
                  const seriesData = allSeries.find(series => {
                    const seriesNameMatch = series["Series Name"] === seriesNameWithoutUnit;
                    const seriesCodeMatch = series["Series Code"]?.includes(seriesNameWithoutUnit);
                    return seriesNameMatch || seriesCodeMatch;
                  });

                  let seriesDescription;

                  // Check multiple possible locations for the description
                  if (seriesData?.series_data?.content?.seriesDescription) {
                    seriesDescription = seriesData.series_data.content.seriesDescription;
                  } else if (selectedCategoryData?.available_specs) {
                    // Try to find in available_specs with partial matching
                    const matchingSpec = selectedCategoryData.available_specs.find(spec => 
                      spec.toLowerCase().includes(seriesNameWithoutUnit.toLowerCase())
                    );
                    if (matchingSpec) {
                      seriesDescription = matchingSpec;
                    }
                  }

                  // If still no description found, check if it's a cost structure type
                  if (!seriesDescription && costStructureMovements) {
                    const costStructureItem = costStructureMovements.find(item => 
                      item.name === seriesNameWithoutUnit ||
                      item.cost_structure_type === seriesNameWithoutUnit
                    );
                    if (costStructureItem) {
                      seriesDescription = costStructureItem.description || 
                                        `${costStructureItem.cost_structure_type} price movement`;
                    }
                  }

                  return (
                    <MUITooltip
                      key={`tooltip-${index}`}
                      title={seriesDescription || "No description available"}
                      arrow
                    >
                      <div
                        key={`item-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: "10px",
                            height: "10px",
                            backgroundColor: entry.color,
                            marginRight: "8px",
                            borderRadius: "50%",
                          }}
                        />
                        <span>{entry.value}</span>
                      </div>
                    </MUITooltip>
                  );
                })}
              </LegendWrapper>
            )}
          />

          {mainCategoryData && mainCategoryData.series_data?.content?.points && (
            <Line
              type="basis"
              dataKey={mainCategoryData["Series Name"]}
              stroke={colors[0]}
              name={`${mainCategoryData["Series Name"]} (${
                mainCategoryData.series_data?.Unit || ""
              })`}
              dot={{ r: 2, fill: colors[0] }}
              activeDot={{ r: 4 }}
              strokeWidth={2}
            />
          )}

          {costMaterialData.map((costMaterial, index) =>
            costMaterial.series_data?.content?.points ? (
              <Line
                key={costMaterial["Series Code"]}
                type="basis"
                dataKey={costMaterial["Series Name"]}
                stroke={colors[(index + 1) % colors.length]}
                name={`${costMaterial["Series Name"]} (${
                  costMaterial.series_data?.Unit || ""
                })`}
                dot={{ r: 2, fill: colors[(index + 1) % colors.length] }}
                activeDot={{ r: 4 }}
                strokeWidth={2}
              />
            ) : null
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderMovements = () => {
    if (!costStructureMovements || !selectedL3Category) return null;
    
    const categoryMovements = costStructureMovements.filter(
      movement => movement.category === selectedL3Category
    );
  
    const averageMovement = categoryMovements.length > 1 
      ? categoryMovements.reduce((sum, movement) => sum + movement.movement, 0) / categoryMovements.length
      : null;
  
    return (
      <MovementContainer>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
          <img src={gs} style={{ width: '20px', height: '20px', marginRight: '5px' }} />
          <div>
            <Typography 
              color="white" 
              sx={{ 
                fontSize: '14px',
                fontStyle: 'Roboto',
                marginBottom: '14px', 
                fontWeight: '500',
                textAlign: 'center',
              }}
            >
              Key commodity price movements
            </Typography>
          </div>
        </div>
        {categoryMovements.length > 0 ? (
          <>
            {categoryMovements.map((movement, index) => (
              movement.movement !== undefined && movement.movement !== null && (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    marginBottom: '8px',
                    borderRadius: '4px',
                    backgroundColor: '#FFFFFF0F',
                    padding: '12px'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        width: '10px', 
                        height: '10px', 
                        borderRadius: '50%', 
                        backgroundColor: movement.chartColor || (movement.movement >= 0 ? '#4CAF50' : '#F44336'),
                        marginRight: '12px' 
                      }} 
                    />
                    <Typography 
                      sx={{ 
                        color: 'white', 
                        fontStyle: 'Roboto', 
                        fontWeight: 500, 
                        fontSize: '14px' 
                      }}
                    >
                      {movement.cost_structure_type}
                    </Typography>
                  </Box>
                  <MUITooltip title={<Typography sx={{ fontStyle: "italic", fontSize: "12px" }}>Calculated as absolute % change from the first to the last month</Typography>} arrow placement="bottom">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px 4px',
                        borderRadius: '4px',
                        transition: 'background-color 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#282c34',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <Typography 
                        sx={{ 
                          color: movement.movement >= 0 ? '#F44336' : '#4CAF50',
                          fontStyle: 'Roboto', 
                          fontWeight: 'bold', 
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {movement.movement.toFixed(2)}%
                        {movement.movement >= 0 ? (
                          <TrendingUp sx={{ fontSize: '18px', marginLeft: '4px' }} />
                        ) : (
                          <TrendingDown sx={{ fontSize: '18px', marginLeft: '4px' }} />
                        )}
                      </Typography>
                      <ArrowUpward
                        sx={{ 
                          fontSize: '18px', 
                          marginLeft: '8px', 
                          visibility: 'hidden',
                          '&:hover': { visibility: 'visible' },
                        }} 
                      />
                    </Box>
                  </MUITooltip>
                </Box>
              )
            ))}
          </>
        ) : (
          <Typography 
            variant="body2" 
            color="white" 
            sx={{ 
              textAlign: 'center', 
              color: '#FFFFFF80' 
            }}
          >
            No data on movements available
          </Typography>
        )}
      </MovementContainer>
    );
  };

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={8}>
          {/* Header Section - Fixed */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              paddingBottom: "16px",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontStyle: "Roboto",
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "18px",
                color: "white",
                marginBottom: "1.5rem",
              }}
            >
              <b>Commodity prices for top categories</b>
            </Typography>
          </div>

          {/* Chart Section - Scrollable */}
          {selectedL3Category ? (
            <Box
              sx={{
                overflowX: "auto", 
                whiteSpace: "nowrap", 
                height: '100%',
                position: 'relative',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent', 
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '100px',
                  visibility: 'hidden',
                },
                '&:hover::-webkit-scrollbar-thumb': {
                  visibility: 'visible',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(0,0,0,0.9)',
                },
                scrollbarWidth: 'none',
                '&:hover': {
                  '&::-webkit-scrollbar-thumb': {
                    visibility: 'visible',
                  },
                },
              }}
            >
              <Box
                sx={{
                  minWidth: '150%', 
                  display: 'inline-block', 
                }}
              >
                {renderChart()}
              </Box>
            </Box>
          ) : (
            <Typography 
              variant="body2" 
              color="white" 
              sx={{ 
                textAlign: 'center', 
                color: '#FFFFFF80' 
              }}
            >
              Please select a category to view commodity price chart
            </Typography>
          )}
        </Grid>

        <Grid item xs={4}>
          {renderMovements()}
        </Grid>
      </Grid>
    </Container>
  );
};


{/* <Container>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    }}
  >
    <Typography
      variant="h6"
      color="white"
      style={{
        fontStyle: "Roboto",
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: "18px",
        color: "white",
        marginBottom: "1.5rem", 
      }}
    >
      <b>Commodity prices for top categories</b>
    </Typography>

    {filteredResults && filteredResults.length > 0 ? (
      <Select
        sx={{
          maxWidth: "120px",
          width: "auto",
          height: "24px",
          padding: "4px 8px",
          fontStyle: "Roboto",
          fontSize: "12px",
          fontWeight: 500,
          gap: "4px",
          borderRadius: "4px",
          opacity: 1,
          background: "#FFFFFF0F",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px solid #E2E8F00F",
          marginBottom: "1.5rem", 
          "& .MuiSelect-select": {
            padding: "0",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
        IconComponent={(props) => (
          <ArrowDropDownCircle
            {...props}
            style={{
              color: "white",
              fontSize: "12px",
            }}
          />
        )}
        MenuProps={{
          PaperProps: {
            style: {
              background: "white",
            },
          },
          MenuListProps: {
            sx: {
              fontStyle: "Roboto",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "15px",
            },
          },
        }}
        value={selectedCategory}
        onChange={handleCategoryChange}
      >
        {filteredResults.map((category) => (
          <MenuItem 
            key={category.category} 
            value={category.category}
            sx={{
              fontSize: "12px",
              fontStyle: "Roboto",
              fontWeight: 500,
              lineHeight: "15px",
            }}
          >
            {category.category}
          </MenuItem>
        ))}
      </Select>
    ) : (
      <LoadingGif/>
    )}
  </div>

  {/* Main container for the chart and movements */}
//   <Grid container spacing={2}>
//     <Grid item xs={8}>
//       {selectedCategory && renderChart()}
//     </Grid>
    
//     <Grid item xs={4}>
//       {renderMovements()}
//     </Grid>
//   </Grid>
// </Container> */}
